import type { ReactNode } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

/**
 * #1898 イベントのカードの中の企画 1 つ分。**見出しの帯 (押すと開閉) + 中身**。
 *
 * 当初は小さな見出しの文字と細い線だけで区切っていたが、中身 (スタンプ枠・景品・
 * ボタン) が縦に長く、**どこからどこまでがどの企画か**が分からなかった。
 * 枠で囲んで見出しを帯にし、企画ごとに畳めるようにする。
 *
 * 見出しは**種類 (小) + 企画名 (大) を必ず両方出す**。企画名が種類と同じ
 * (例: AR宝探し) ときに種類を省くと、その企画だけ見出しの形が変わって見える。
 *
 * 畳んでいるときだけ進捗 (例: 0/3) を見出しに出す。開いているときは中のスタンプ枠が
 * 同じ数字を出すので、2 つ並べない。
 */
export default function StampProgramPanel({
  icon,
  label,
  name,
  description,
  tone = 'navy',
  summary,
  open,
  onToggle,
  children,
}: {
  icon: ReactNode
  /** 企画の種類 (スタンプラリー / AR宝探し / ミニゲーム …) */
  label: string
  /** 企画名 */
  name: string
  description?: string
  tone?: 'navy' | 'gold'
  /** 畳んでいるときに見出しに出す短い状態 (進捗など) */
  summary?: string
  open: boolean
  onToggle: () => void
  children: ReactNode
}) {
  const accent = tone === 'gold' ? 'text-gold' : 'text-navy'
  return (
    <section className="overflow-hidden rounded-2xl border border-navy/10 bg-white">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="tap-effect flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            tone === 'gold' ? 'bg-gold/15' : 'bg-navy/10'
          } ${accent}`}
        >
          {icon}
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block font-body text-xs font-medium ${accent}`}>{label}</span>
          <span className="block font-display text-base leading-snug text-navy">{name}</span>
        </span>
        {!open && summary && (
          <span className="shrink-0 font-display text-sm text-navy">{summary}</span>
        )}
        {open ? (
          <ChevronUp size={18} className="shrink-0 text-text-tertiary" />
        ) : (
          <ChevronDown size={18} className="shrink-0 text-text-tertiary" />
        )}
      </button>
      {open && (
        <div className="flex flex-col gap-4 border-t border-navy/10 bg-navy/5 p-3">
          {description && (
            <p className="px-1 font-body text-xs text-text-secondary">{description}</p>
          )}
          {children}
        </div>
      )}
    </section>
  )
}
